import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useSociogramActions } from '@BUP/hooks/Sociogram/useSociogramContext'
import { useGetShowVistaDocenteFeatureFlag } from '@BUP/hooks/Sociogram/useGetShowVistaDocenteFeatureFlag'
import { OrganizationContext } from '@BUP/contexts/OrganizationContext'
import Table from '..'
import IconCell from './icon-cell.jsx'
import NameCell from './name-cell.jsx'
import StatusCell from './status-cell.jsx'
import ParticipantsCell from './participants-cell.jsx'
import ActionsCell from './actions-cell.jsx'
import { multiCourse } from '@BUP/constants'
import styles from './inner-table.module.scss'
import ShowTeacherResultsButton from './show-teacher-results-button'

const InnerTable = ({
  loading,
  surveysData,
  sociogram,
  errorResultRecommendations
}) => {
  const i18n = useTranslation()

  const { setSelectedSurvey } = useSociogramActions()
  const {
    showVistaDocenteFeatureFlag
  } = useGetShowVistaDocenteFeatureFlag({})
  const organization = useContext(OrganizationContext)

  if (!surveysData || surveysData.length === 0) return null

  const { type: organizationType } = organization
  const isMultiCourse = organizationType === multiCourse

  const hasNonEmptyObject = (obj) =>
    !!obj && typeof obj === 'object' && Object.keys(obj).length > 0

  const hasNonEmptyArray = (arr) =>
    Array.isArray(arr) && arr.length > 0

  const setFontColor = (isActive, isDone, results) => {
    if (!isActive && isDone && results && results.length === 0) return '#17BEBB'
    if (!isActive && isDone && results && results.length > 0) return '#6441A5'
    return '#898A89'
  }

  const setFontWeight = (isActive, isDone, results) => {
    if (!isActive && isDone && results && results.length > 0) return 'bold'
    if (!isActive && isDone && results && results.length === 0) return 'bold'
    return '400'
  }

  const columns = [
    {
      Header: '',
      accessor: 'id',
      disableSortBy: true,
      Cell: ({ row }) => {
        const { original: survey } = row
        return <IconCell survey={survey} />
      }
    },
    {
      Header: isMultiCourse
        ? i18n.t('common.resources.course.attributes.tags.nivel.label')
        : i18n.t('common.resources.course.label'),
      accessor: 'name',
      Cell: ({ row }) => {
        const { original: survey } = row
        return (
          <NameCell
            survey={survey}
            isMultiCourse={isMultiCourse}
            setFontColor={setFontColor}
            setFontWeight={setFontWeight}
          />
        )
      }
    },
    {
      Header: `${i18n.t('pages.data.sociogram.activeSociogramEce.table.headerState')}`,
      accessor: 'isActive',
      disableSortBy: true,
      Cell: ({ value: isActive, row }) => {
        const { original: survey } = row
        return (
          <StatusCell
            survey={survey}
            isActive={isActive}
            setFontColor={setFontColor}
            setFontWeight={setFontWeight}
          />
        )
      }
    },
    {
      Header: `${i18n.t('pages.data.sociogram.activeSociogramEce.table.headerResponses')}`,
      accessor: 'participants',
      disableSortBy: true,
      Cell: ({ row, value: participants }) => {
        const { original: survey } = row
        if (!survey) {
          return (
            <span className={styles.actions_cell_text}>
              {i18n.t('pages.data.sociogram.activeSociogramEce.noSurveyMessage')}
            </span>
          )
        }
        const { participantsTerminated } = survey
        return (
          <ParticipantsCell
            survey={survey}
            participants={participants}
            setFontColor={setFontColor}
            setSelectedSurvey={setSelectedSurvey}
            participantsTerminated={participantsTerminated.length}
          />
        )
      }
    },
    {
      Header: `${i18n.t('pages.data.sociogram.activeSociogramEce.table.actionsHeader')}`,
      accessor: 'isDone',
      disableSortBy: true,
      Cell: ({ row }) => {
        const { original: survey } = row
        if (!survey) {
          return (
            <span className={styles.actions_cell_text}>
              {i18n.t('pages.data.sociogram.activeSociogramEce.noSurveyMessage')}
            </span>
          )
        }
        const { participantsTerminated } = survey

        return (
          <ActionsCell
            survey={survey}
            isMultiCourse={isMultiCourse}
            sociogram={sociogram}
            errorResultRecommendations={errorResultRecommendations}
            participantsTerminated={participantsTerminated.length}
          />
        )
      }
    }
  ]

  const hasTeacherData = surveysData.some(
    (survey) => hasNonEmptyObject(survey.teacherData)
  )

  const hasResults = surveysData.some(
    (survey) => hasNonEmptyArray(survey.results)
  )

  const isShowVistaDocenteEnabled = showVistaDocenteFeatureFlag?.enabled === true

  if (!isMultiCourse && hasTeacherData && hasResults && isShowVistaDocenteEnabled) {
    columns.push({
      Header: `${i18n.t('pages.data.sociogram.activeSociogramEce.table.resultsMainTeacherHeader')}`,
      accessor: 'teacherData',
      disableSortBy: true,
      headerClassName: styles.center_header,
      Cell: ({ row }) => {
        const { original: survey } = row
        if (!survey) {
          return (
            <span className={styles.actions_cell_text}>
              {i18n.t('pages.data.sociogram.activeSociogramEce.noSurveyMessage')}
            </span>
          )
        }

        const teacherData = survey.teacherData
        const results = survey.results

        const rowHasTeacherData = hasNonEmptyObject(teacherData)
        const rowHasResults = hasNonEmptyArray(results)

        if (!rowHasTeacherData || !rowHasResults) return null

        return <ShowTeacherResultsButton survey={survey} />
      }
    })
  }

  return (
    <div data-testid='sociogramInnerTable'>
      <Table
        data={surveysData}
        columnsArray={columns}
        smallFirstChild
        loading={loading}
      />
    </div>
  )
}

export default InnerTable
